import React, { useEffect } from 'react';
import { SEO } from '../components/SEO';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { siteConfig, services } from '../data/mock';
import { MessageCircle, Clock, CheckCircle } from 'lucide-react';

export const AgendamentoWhatsApp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleServiceClick = (serviceName) => {
    const message = `Olá! Gostaria de agendar o serviço de ${serviceName}. Poderia me passar mais informações?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`${siteConfig.whatsappLink}?text=${encodedMessage}`, '_blank');
  };

  return (
    <>
      <SEO 
        title="Agendamento WhatsApp Box7go - Agende Lava Jato e Estética Automotiva em Goiânia"
        description="Agende seu serviço de lava jato e estética automotiva pelo WhatsApp. Rápido, fácil e conveniente. Box7go Goiânia - (62) 98241-6008"
        keywords="agendar lava jato goiania, whatsapp box7go, agendamento online lava jato, agendar estética automotiva"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#25D366] via-[#20BA5A] to-[#1BA952] py-16">
        <div className="container mx-auto px-4 text-center">
          <MessageCircle className="w-20 h-20 text-white mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Agende pelo WhatsApp
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
            Rápido, fácil e conveniente. Escolha seu serviço e fale conosco agora!
          </p>
          <WhatsAppButton 
            text="Iniciar Conversa"
            className="bg-white text-[#25D366] hover:bg-gray-100 text-lg px-8 py-4"
          />
        </div>
      </section>

      {/* Como Funciona */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Como Funciona?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Agendar seu serviço é simples e rápido
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#25D366] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Escolha o Serviço</h3>
              <p className="text-gray-600 text-sm">
                Selecione o serviço que deseja na lista abaixo
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#25D366] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Clique no Botão</h3>
              <p className="text-gray-600 text-sm">
                Será direcionado ao WhatsApp com mensagem pronta
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#25D366] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Converse Conosco</h3>
              <p className="text-gray-600 text-sm">
                Nossa equipe responderá rapidamente
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#25D366] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Confirme o Horário</h3>
              <p className="text-gray-600 text-sm">
                Escolha o melhor dia e horário para você
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Escolha Seu Serviço
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Clique no serviço desejado para iniciar o agendamento
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="relative h-40">
                  <img 
                    src={service.image} 
                    alt={service.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-xl text-gray-900 mb-2">{service.name}</h3>
                  <p className="text-gray-600 text-sm mb-3">{service.shortDesc}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <Clock className="w-4 h-4" />
                    <span>{service.duration}</span>
                  </div>
                  <button
                    onClick={() => handleServiceClick(service.name)}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Agendar este Serviço
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vantagens */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Vantagens de Agendar pelo WhatsApp
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-[#25D366] mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-900 mb-2">Resposta Rápida</h3>
              <p className="text-gray-600">
                Atendimento ágil durante horário comercial
              </p>
            </div>
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-[#25D366] mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-900 mb-2">Sem Complicação</h3>
              <p className="text-gray-600">
                Não precisa preencher formulários longos
              </p>
            </div>
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-[#25D366] mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-900 mb-2">Flexibilidade</h3>
              <p className="text-gray-600">
                Escolha o melhor horário para você
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-br from-[#25D366] via-[#20BA5A] to-[#1BA952]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Pronto para agendar?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Fale conosco agora e garanta seu horário
          </p>
          <WhatsAppButton 
            text="Falar no WhatsApp Agora"
            className="bg-white text-[#25D366] hover:bg-gray-100 text-lg px-8 py-4"
          />
        </div>
      </section>
    </>
  );
};
